import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export const useInfiniteTickets = ({
    queryBuilder,
    pageSize = 20,
    enabled = true,
    initialLoad = true
}, deps = []) => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const fetchTickets = useCallback(async (isLoadMore = false) => {
        if (!enabled) return;
        if (isLoadMore && (!hasMore || loadingMore)) return;
        if (!isLoadMore && loading) return;

        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const currentPage = isLoadMore ? page + 1 : 0;
            const from = currentPage * pageSize;
            const to = from + pageSize - 1;

            let query = supabase
                .from('tickets')
                .select('*', { count: 'exact' });

            if (queryBuilder) {
                query = queryBuilder(query);
            }

            // Always order by latest update or creation by default if not specified in builder
            // But usually builder handles sort. We append range here.
            query = query.range(from, to);

            const { data, error, count } = await query;

            if (error) throw error;

            if (isMounted.current) {
                if (isLoadMore) {
                    setTickets(prev => [...prev, ...data]);
                    setPage(currentPage);
                } else {
                    setTickets(data);
                    setPage(0);
                    setTotalCount(count);
                }

                // If we got fewer items than requested, we reached the end
                setHasMore(data.length === pageSize);
            }
        } catch (err) {
            console.error("Error fetching tickets:", err);
            if (isMounted.current) {
                setError(err);
                toast.error("Erro ao carregar tickets.");
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [enabled, pageSize, page, hasMore, loading, loadingMore, ...deps]);

    // Reset and fetch when dependencies change
    useEffect(() => {
        if (initialLoad) {
            fetchTickets(false);
        }
    }, [...deps]);

    const loadMore = () => fetchTickets(true);
    const refresh = () => fetchTickets(false);

    return {
        tickets,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        refresh,
        totalCount,
        error
    };
};
